import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import apiCompanies from '../features/configuration/services/company'

export const UNIT_NAMING_CACHE_KEY = 'unitNamingTypeCache'
const DEFAULT_NAMING_TYPE = 'branch_department'

const UNIT_NAMING_CHANGED = 'unitNamingTypeChanged'

/**
 * Clears cached unit naming type and notifies all useUnitNaming subscribers to refetch.
 * Call after company create or PATCH that changes unit_naming_type.
 */
export function invalidateUnitNamingCache() {
  sessionStorage.removeItem(UNIT_NAMING_CACHE_KEY)
  window.dispatchEvent(new CustomEvent(UNIT_NAMING_CHANGED))
}

const useUnitNaming = () => {
  const { t } = useTranslation()
  const [namingType, setNamingType] = useState(DEFAULT_NAMING_TYPE)
  const [loading, setLoading] = useState(true)

  const fetchNaming = useCallback((options = {}) => {
    const silent = Boolean(options.silent)
    if (!silent) setLoading(true)
    apiCompanies
      .getCompanyProfileConfig()
      .then((res) => {
        const resolved = res?.unitNamingType || DEFAULT_NAMING_TYPE
        sessionStorage.setItem(UNIT_NAMING_CACHE_KEY, resolved)
        setNamingType(resolved)
      })
      .catch(() => {
        if (!silent) setNamingType(DEFAULT_NAMING_TYPE)
      })
      .finally(() => {
        if (!silent) setLoading(false)
      })
  }, [])

  useEffect(() => {
    const cached = sessionStorage.getItem(UNIT_NAMING_CACHE_KEY)
    if (cached) {
      setNamingType(cached)
      setLoading(false)
      fetchNaming({ silent: true })
    } else {
      fetchNaming()
    }
    const onChanged = () => {
      fetchNaming()
    }
    window.addEventListener(UNIT_NAMING_CHANGED, onChanged)
    return () => window.removeEventListener(UNIT_NAMING_CHANGED, onChanged)
  }, [fetchNaming])

  const prefix = `unitNaming.${namingType}`

  return {
    parentUnitLabel: t(`${prefix}.parentUnit`),
    childUnitLabel: t(`${prefix}.childUnit`),
    parentPluralLabel: t(`${prefix}.parentPlural`),
    childPluralLabel: t(`${prefix}.childPlural`),
    unitsPanelLabel: t(`${prefix}.unitsPanel`),
    parentManagementLabel: t(`${prefix}.parentManagement`),
    childManagementLabel: t(`${prefix}.childManagement`),
    backToParentsLabel: t(`${prefix}.backToParents`),
    schemeLabel: t(`${prefix}.schemeLabel`),
    parentFieldNameLabel: t(`${prefix}.parentFieldName`),
    parentFieldNumberLabel: t(`${prefix}.parentFieldNumber`),
    addParentTitleLabel: t(`${prefix}.addParentTitle`),
    editParentTitleLabel: t(`${prefix}.editParentTitle`),
    addParentSuccessMessage: t(`${prefix}.addParentSuccess`),
    editParentSuccessMessage: t(`${prefix}.editParentSuccess`),
    childFieldNameLabel: t(`${prefix}.childFieldName`),
    childFieldNumberLabel: t(`${prefix}.childFieldNumber`),
    addChildTitleLabel: t(`${prefix}.addChildTitle`),
    editChildTitleLabel: t(`${prefix}.editChildTitle`),
    addChildSuccessMessage: t(`${prefix}.addChildSuccess`),
    editChildSuccessMessage: t(`${prefix}.editChildSuccess`),
    namingType,
    loading,
  }
}

export default useUnitNaming
